import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { subscribeToRouteClosures, replyToDispute } from '../../services/routeClosuresService';
import { getMessagingInstance, getToken, FCM_VAPID_KEY } from '../../config/firebase';
import { TIPOS_AUSENCIA, getTipoAusencia } from '../../utils/tiposAusencia';

const getSpainToday = () =>
  new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Madrid' }).format(new Date());

// La mayoría de técnicos usan Android/Chrome, no iPhone (Rafa, 2026-10-15) —
// el aviso de "bloqueadas" daba por hecho Safari/iOS para todo el mundo. En
// Chrome (Android o escritorio) se desbloquea desde el icono de info del
// sitio junto a la URL, no desde unos "Ajustes" del sistema.
const esSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '');

function FichajeCard({ user }) {
  const [fichaje,  setFichaje]  = useState(undefined); // undefined=cargando, null=no existe
  const [loading,  setLoading]  = useState(true);
  const [acting,   setActing]   = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const today = getSpainToday();

  const loadFichaje = useCallback(async () => {
    if (!user?.employeeId) return;
    try {
      const resp = await fetch(`/.netlify/functions/getFichajes?fecha=${today}`, { headers: { ...authService.authHeader() } });
      const data = await resp.json();
      if (data.success) {
        const mine = (data.fichajes || []).find(f => String(f.tecnicoId) === String(user.employeeId));
        setFichaje(mine || null);
      }
    } catch (e) {
      console.error('Error cargando fichaje:', e);
    } finally {
      setLoading(false);
    }
  }, [user?.employeeId, today]);

  useEffect(() => { loadFichaje(); }, [loadFichaje]);

  const handleFichar = async (tipo) => {
    setActing(true);
    setErrorMsg('');
    try {
      const resp = await fetch('/.netlify/functions/createFichaje', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({
          tipo,
          tecnicoId:    String(user.employeeId),
          tecnicoNombre: user.name || '',
          fecha:         today,
          creadoPor:    'tecnico',
        }),
      });
      const data = await resp.json();
      if (data.success) {
        setFichaje(data.fichaje);
      } else {
        setErrorMsg(data.error || 'Error al fichar');
        if (resp.status === 409) loadFichaje(); // sync state on conflict
      }
    } catch (e) {
      setErrorMsg('Error de conexión');
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-4 shadow-sm border flex items-center gap-3">
        <div className="w-5 h-5 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin flex-shrink-0" />
        <p className="text-sm text-gray-400">Cargando fichaje...</p>
      </div>
    );
  }

  const isCerrado = fichaje?.estado === 'cerrado' || fichaje?.estado === 'corregido';
  const isActivo  = fichaje?.estado === 'activo';

  return (
    <div className={`rounded-2xl p-4 shadow-sm border ${
      isCerrado ? 'bg-green-50 border-green-200'
      : isActivo ? 'bg-amber-50 border-amber-200'
      : 'bg-white border-gray-100'
    }`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isCerrado ? 'bg-green-100' : isActivo ? 'bg-amber-100' : 'bg-red-50'
        }`}>
          <svg className={`w-5 h-5 ${isCerrado ? 'text-green-600' : isActivo ? 'text-amber-600' : 'text-red-600'}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 text-sm">Fichaje del día</p>
          {isCerrado && (
            <p className="text-xs text-green-700 font-semibold mt-0.5">
              {fichaje.entradaHora} → {fichaje.salidaHora}
              {fichaje.duracionMinutos != null && ` (${Math.floor(fichaje.duracionMinutos / 60)}h ${fichaje.duracionMinutos % 60}m)`}
            </p>
          )}
          {isActivo && (
            <p className="text-xs text-amber-700 font-semibold mt-0.5">
              Entrada a las {fichaje.entradaHora} — Jornada en curso
            </p>
          )}
          {!fichaje && (
            <p className="text-xs text-gray-400 mt-0.5">Aún no has fichado hoy</p>
          )}
        </div>
        {isCerrado && (
          <span className="flex-shrink-0 text-green-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </span>
        )}
      </div>

      {errorMsg && (
        <p className="text-xs text-red-600 mb-2 text-center">{errorMsg}</p>
      )}

      {!isCerrado && (
        <button
          onClick={() => handleFichar(fichaje ? 'salida' : 'entrada')}
          disabled={acting}
          className={`w-full py-3 font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${
            fichaje
              ? 'bg-amber-500 hover:bg-amber-600 text-white'
              : 'bg-red-600 hover:bg-red-700 text-white'
          }`}
        >
          {acting && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
          {fichaje ? 'Fichar salida' : 'Fichar entrada'}
        </button>
      )}
    </div>
  );
}

const toDate = (d) => d?.toDate ? d.toDate() : d instanceof Date ? d : d ? new Date(d) : null;

const isSameDay = (a, b) => {
  const da = toDate(a); const db = toDate(b);
  return da && db && da.toDateString() === db.toDateString();
};

const getCompletionPct = (assigned, completed) => {
  const a = Number(assigned) || 0;
  const c = Number(completed) || 0;
  return a > 0 ? Math.round((c / a) * 100) : 0;
};

const getWeekDays = () => {
  const today = new Date();
  const dow = today.getDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(today);
  monday.setDate(today.getDate() + offset);
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 5 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
};

const pctColor = (pct) => {
  if (pct >= 100) return 'bg-green-500 text-white';
  if (pct >= 75)  return 'bg-green-200 text-green-800';
  if (pct > 0)    return 'bg-amber-400 text-white';
  return 'bg-red-400 text-white';
};

const pctBadge = (pct) => {
  if (pct >= 100) return 'bg-green-100 text-green-700';
  if (pct > 0)    return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};

function ClosureDetailModal({ closure, onClose }) {
  const toD = (d) => d?.toDate ? d.toDate() : d instanceof Date ? d : d ? new Date(d) : null;
  const dateObj    = toD(closure.date);
  const timeObj    = toD(closure.createdAt || closure.date);
  const assigned   = Number(closure.assigned)  || 0;
  const completed  = Number(closure.completed) || 0;
  const completion = assigned > 0 ? Math.round((completed / assigned) * 100) : 0;
  // `cash` es un booleano (¿hubo algo de efectivo?), no el importe — usarlo
  // aquí hacía que Number(true) diera "1€" en vez del importe real guardado
  // en `efectivo` (ver manageRouteClosure.js: cash = efectivo > 0).
  const efectivo   = closure.efectivo;

  const barColor   = completion >= 100 ? 'bg-green-500' : completion >= 80 ? 'bg-amber-500' : 'bg-red-500';
  const labelColor = completion >= 100 ? 'text-green-700' : completion >= 80 ? 'text-amber-700' : 'text-red-700';

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/50" onClick={onClose}>
      <div
        className="bg-white w-full rounded-t-3xl shadow-2xl max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mt-3" />

        {/* Header */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 mx-4 mt-3 rounded-2xl p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-white font-bold text-base capitalize">
                {dateObj ? dateObj.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Sin fecha'}
              </p>
              {timeObj && (
                <p className="text-red-200 text-xs mt-0.5">
                  Cerrada a las {timeObj.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white p-1">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4 space-y-4">

          {/* Barra de completitud */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <span className="text-sm font-semibold text-gray-700">Órdenes completadas</span>
              <span className={`text-sm font-bold ${labelColor}`}>{completion}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all ${barColor}`}
                style={{ width: `${Math.min(completion, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{completed} completadas</span>
              <span>{assigned} asignadas</span>
            </div>
          </div>

          {/* Efectivo */}
          {efectivo !== undefined && efectivo !== null && Number(efectivo) > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-green-800">Efectivo cobrado</span>
              <span className="text-lg font-bold text-green-700">€{Number(efectivo).toFixed(2)}</span>
            </div>
          )}

          {/* Nota del director */}
          {closure.notes ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-base">📋</span>
                <span className="text-sm font-bold text-amber-800">Nota del director</span>
              </div>
              <p className="text-sm text-amber-900 leading-relaxed whitespace-pre-wrap">{closure.notes}</p>
            </div>
          ) : (
            <div className="text-center py-3 text-xs text-gray-400">Sin notas del director</div>
          )}

          <button
            onClick={onClose}
            className="w-full py-3 bg-gray-100 hover:bg-gray-200 font-semibold rounded-xl transition-colors text-sm"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function NotificationCard({ user }) {
  const getPermission = () => {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission; // 'default' | 'granted' | 'denied'
  };

  const [status, setStatus] = useState(getPermission);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const handleEnable = useCallback(async () => {
    setLoading(true);
    setErrMsg('');
    try {
      const permission = await Notification.requestPermission();
      setStatus(permission);
      if (permission !== 'granted') { setLoading(false); return; }

      const messaging = await getMessagingInstance();
      if (!messaging) { setErrMsg('Mensajería no disponible en este navegador'); setLoading(false); return; }

      const swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      const token = await getToken(messaging, { vapidKey: FCM_VAPID_KEY, serviceWorkerRegistration: swReg });

      if (!token) { setErrMsg('No se pudo obtener token FCM'); setLoading(false); return; }
      if (!user?.id) { setErrMsg('Error: sesión sin ID de usuario'); setLoading(false); return; }

      const resp = await fetch('/.netlify/functions/saveFcmToken', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ token, technicianId: user.id, technicianName: user.name || '', employeeId: user.employeeId }),
      });
      if (resp.ok) {
        setDone(true);
      } else {
        const data = await resp.json().catch(() => ({}));
        setErrMsg(`Error al guardar: ${data.error || resp.status}`);
      }
    } catch (e) {
      setErrMsg(`Error: ${e.message}`);
    }
    setLoading(false);
  }, [user]);

  if (status === 'unsupported') return null;

  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>
        <div>
          <p className="text-sm font-bold text-gray-800">Notificaciones push</p>
          <p className="text-xs text-gray-400">Avisos del director en tu móvil</p>
        </div>
        <div className="ml-auto flex-shrink-0">
          {status === 'granted'
            ? <span className="text-xs bg-green-100 text-green-700 font-semibold px-2 py-1 rounded-full">Activadas</span>
            : status === 'denied'
            ? <span className="text-xs bg-red-100 text-red-700 font-semibold px-2 py-1 rounded-full">Bloqueadas</span>
            : <span className="text-xs bg-gray-100 text-gray-500 font-semibold px-2 py-1 rounded-full">Sin activar</span>
          }
        </div>
      </div>

      {status === 'granted' && done && (
        <p className="text-xs text-green-600 text-center py-1">✓ Registrado correctamente</p>
      )}
      {status === 'granted' && !done && (
        <button
          onClick={handleEnable}
          disabled={loading}
          className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 font-semibold rounded-xl text-sm transition-colors"
        >
          {loading ? 'Registrando…' : 'Volver a registrar dispositivo'}
        </button>
      )}
      {status === 'denied' && (
        <p className="text-xs text-red-600 text-center py-1">
          {esSafari()
            ? 'Ve a Ajustes → Safari → Notificaciones para desbloquearlas'
            : 'Toca el icono 🔒 junto a la dirección web → Permisos → Notificaciones, para desbloquearlas'}
        </p>
      )}
      {status === 'default' && (
        <button
          onClick={handleEnable}
          disabled={loading}
          className="w-full py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold rounded-xl text-sm transition-colors"
        >
          {loading ? 'Activando…' : 'Activar notificaciones'}
        </button>
      )}
      {errMsg && (
        <p className="text-xs text-red-600 text-center mt-1 break-all">{errMsg}</p>
      )}
    </div>
  );
}

// Autoservicio del técnico: registra una ausencia para sí mismo (el
// tecnicoId real lo fuerza el servidor a partir de la sesión, ver
// manageAusencias.js — nunca se manda el de otro). Vacaciones nace
// "pendiente" y avisa a admin/coordinador_operaciones (notificaciones +
// campanita); cualquier otro tipo (baja médica, permiso...) se da por buena
// al momento, sin aprobación.
function RegistrarAusenciaModal({ user, onClose, onSaved }) {
  const [tipo,        setTipo]        = useState('vacaciones');
  const [fechaInicio, setFechaInicio] = useState(getSpainToday());
  const [fechaFin,    setFechaFin]    = useState(getSpainToday());
  const [notas,       setNotas]       = useState('');
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState('');

  const handleSave = async () => {
    if (fechaFin < fechaInicio) { setError('La fecha de fin no puede ser anterior a la de inicio'); return; }
    setSaving(true);
    setError('');
    try {
      const resp = await fetch('/.netlify/functions/manageAusencias', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ tipo, fechaInicio, fechaFin, notas, tecnicoNombre: user?.name || '' }),
      });
      const data = await resp.json();
      if (!data.success) throw new Error(data.error || 'Error al registrar la ausencia');
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b sticky top-0 bg-white z-10">
          <h2 className="font-bold text-gray-900 flex-1">Registrar ausencia</h2>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-700">Tipo de ausencia</label>
            <select
              value={tipo}
              onChange={e => setTipo(e.target.value)}
              className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300"
            >
              {TIPOS_AUSENCIA.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            {tipo === 'vacaciones' && (
              <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-1.5 mt-1">
                Las vacaciones quedan pendientes de aprobación por dirección de operaciones.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">Desde</label>
              <input
                type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-semibold text-gray-700">Hasta</label>
              <input
                type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)}
                className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-300"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-semibold text-gray-700">Notas (opcional)</label>
            <textarea
              value={notas} onChange={e => setNotas(e.target.value)} rows={2}
              placeholder="Detalles adicionales..."
              className="w-full text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
            />
          </div>

          {error && <p className="text-sm text-red-600 bg-red-100 rounded-xl px-3 py-2">{error}</p>}

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-bold rounded-xl text-sm transition-colors"
          >
            {saving ? 'Guardando...' : 'Registrar ausencia'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MiHistorialFichajes({ user }) {
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [fichajes, setFichajes] = useState([]);
  const [loading, setLoading]   = useState(false);

  const fichajeId = user?.role === 'tecnico' && user?.employeeId
    ? String(user.employeeId)
    : String(user?.id || '');

  const load = useCallback(async () => {
    if (!fichajeId) return;
    setLoading(true);
    try {
      const resp = await fetch(`/.netlify/functions/getFichajes?mes=${selectedMonth}`, { headers: { ...authService.authHeader() } });
      const data = await resp.json();
      if (data.success) {
        const mine = (data.fichajes || [])
          .filter(f => String(f.tecnicoId) === fichajeId)
          .sort((a, b) => b.fecha.localeCompare(a.fecha));
        setFichajes(mine);
      }
    } catch (e) {
      console.error('Error cargando historial:', e);
    } finally {
      setLoading(false);
    }
  }, [fichajeId, selectedMonth]);

  useEffect(() => { load(); }, [load]);

  const totalEfectivo = fichajes.reduce((s, f) => s + (f.duracionMinutos || 0), 0);
  const totalDescanso = fichajes.reduce((s, f) => s + (f.duracionDescansoMinutos || 0), 0);
  const diasCerrados  = fichajes.filter(f => f.estado === 'cerrado' || f.estado === 'corregido').length;

  const fmtMin = (m) => `${Math.floor(m / 60)}h ${m % 60 > 0 ? `${m % 60}m` : ''}`.trim();

  const changeMonth = (delta) => {
    const [y, mo] = selectedMonth.split('-').map(Number);
    const d = new Date(y, mo - 1 + delta, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const monthLabel = (() => {
    const [y, mo] = selectedMonth.split('-').map(Number);
    return new Date(y, mo - 1, 1).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });
  })();

  return (
    <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="font-bold text-gray-900 text-sm">Mi historial de fichajes</h3>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => changeMonth(-1)}
            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 font-bold leading-none">‹</button>
          <span className="text-xs font-semibold text-gray-700 capitalize px-1 min-w-[90px] text-center">
            {monthLabel}
          </span>
          <button onClick={() => changeMonth(1)}
            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 font-bold leading-none">›</button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <div className="w-5 h-5 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : fichajes.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <p className="text-sm">Sin fichajes registrados este mes</p>
        </div>
      ) : (
        <>
          <div className="px-4 py-3 bg-blue-50 border-b border-blue-100 grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-xs text-blue-500 font-medium">Días</p>
              <p className="text-lg font-bold text-blue-800">{diasCerrados}</p>
            </div>
            <div>
              <p className="text-xs text-blue-500 font-medium">Horas efectivas</p>
              <p className="text-lg font-bold text-blue-800">{fmtMin(totalEfectivo)}</p>
            </div>
            <div>
              <p className="text-xs text-blue-500 font-medium">Descanso</p>
              <p className="text-lg font-bold text-blue-800">{fmtMin(totalDescanso)}</p>
            </div>
          </div>
          <div className="divide-y divide-gray-50">
            {fichajes.map(f => {
              const isClosed = f.estado === 'cerrado' || f.estado === 'corregido';
              const date = new Date(f.fecha + 'T12:00:00');
              return (
                <div key={f.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 capitalize">
                      {date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })}
                    </p>
                    {isClosed ? (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {f.entradaHora} → {f.salidaHora}
                        {f.duracionMinutos != null && (
                          <span className="ml-1 text-green-600 font-medium">
                            {fmtMin(f.duracionMinutos)} efectivas
                          </span>
                        )}
                      </p>
                    ) : (
                      <p className="text-xs text-amber-600 font-medium mt-0.5">
                        Entrada {f.entradaHora} — Jornada abierta
                      </p>
                    )}
                  </div>
                  <span className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${
                    isClosed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {isClosed ? 'Cerrado' : 'Activo'}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default function TechnicianDashboardView() {
  const { user } = useAuth();
  const [allClosures, setAllClosures]     = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedClosure, setSelectedClosure] = useState(null);
  const [disputeReplies, setDisputeReplies] = useState({});   // { closureId: text }
  const [sendingReply, setSendingReply]     = useState({});   // { closureId: bool }
  const [ausencias, setAusencias]           = useState([]);
  const [showAusenciaModal, setShowAusenciaModal] = useState(false);

  useEffect(() => {
    const unsub = subscribeToRouteClosures(
      (data) => { setAllClosures(data); setLoading(false); },
      ()     => { setError('Error al cargar cierres'); setLoading(false); }
    );
    return unsub;
  }, []);

  // Vacaciones/bajas propias del mes que se está viendo en el calendario —
  // mismo endpoint que usa oficina para registrarlas (manageAusencias.js),
  // aquí solo en lectura y filtradas a las del propio técnico.
  const cargarAusencias = useCallback(() => {
    const employeeId = user?.employeeId;
    if (!employeeId) return;
    const mes = `${calendarMonth.getFullYear()}-${String(calendarMonth.getMonth() + 1).padStart(2, '0')}`;
    fetch(`/.netlify/functions/manageAusencias?mes=${mes}`, { headers: { ...authService.authHeader() } })
      .then(r => r.json())
      .then(data => {
        if (!data.success) { setAusencias([]); return; }
        setAusencias((data.ausencias || []).filter(a => String(a.tecnicoId) === String(employeeId)));
      })
      .catch(() => setAusencias([]));
  }, [user?.employeeId, calendarMonth]);

  useEffect(() => { cargarAusencias(); }, [cargarAusencias]);

  const myClosures = useMemo(() =>
    allClosures
      .filter(c => String(c.employeeId) === String(user?.employeeId))
      .sort((a, b) => (toDate(b.date) || 0) - (toDate(a.date) || 0)),
    [allClosures, user?.employeeId]
  );

  const myDisputes = useMemo(() =>
    myClosures.filter(c => c.disputeStatus === 'open'),
    [myClosures]
  );

  const handleSendReply = async (closureId) => {
    const reply = (disputeReplies[closureId] || '').trim();
    if (!reply) return;
    setSendingReply(prev => ({ ...prev, [closureId]: true }));
    try {
      await replyToDispute(closureId, reply);
      setDisputeReplies(prev => ({ ...prev, [closureId]: '' }));
    } catch (e) {
      console.error('Error enviando respuesta:', e);
    } finally {
      setSendingReply(prev => ({ ...prev, [closureId]: false }));
    }
  };

  const weekDays = useMemo(getWeekDays, []);

  const weekStats = useMemo(() => {
    const weekClosures = myClosures.filter(c => {
      const d = toDate(c.date);
      return d && d >= weekDays[0] && d < new Date(weekDays[4].getTime() + 86400000);
    });
    const assigned  = weekClosures.reduce((s, c) => s + (Number(c.assigned)  || 0), 0);
    const completed = weekClosures.reduce((s, c) => s + (Number(c.completed) || 0), 0);
    return {
      days: weekClosures.length,
      assigned,
      completed,
      pct: assigned > 0 ? Math.round((completed / assigned) * 100) : 0,
    };
  }, [myClosures, weekDays]);

  const monthStats = useMemo(() => {
    const now   = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthClosures = myClosures.filter(c => {
      const d = toDate(c.date);
      return d && d >= start && d <= now;
    });
    const assigned  = monthClosures.reduce((s, c) => s + (Number(c.assigned)  || 0), 0);
    const completed = monthClosures.reduce((s, c) => s + (Number(c.completed) || 0), 0);
    return {
      days: monthClosures.length,
      assigned,
      completed,
      pct: assigned > 0 ? Math.round((completed / assigned) * 100) : 0,
    };
  }, [myClosures]);

  const calendarData = useMemo(() => {
    const year  = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const last  = new Date(year, month + 1, 0).getDate();
    const days  = [];
    for (let d = 1; d <= last; d++) {
      const date = new Date(year, month, d);
      // Las ausencias sí se muestran en fin de semana (una baja/vacaciones no
      // se pausa el sábado) — el resto del calendario solo mira laborables.
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const ausencia = ausencias.find(a => a.fechaInicio <= dateStr && a.fechaFin >= dateStr);
      if ((date.getDay() === 0 || date.getDay() === 6) && !ausencia) continue;
      const closure = myClosures.find(c => isSameDay(toDate(c.date), date));
      days.push({
        date,
        isToday:  isSameDay(date, new Date()),
        isFuture: date > new Date(),
        closure,
        ausencia,
      });
    }
    return days;
  }, [myClosures, calendarMonth, ausencias]);

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" />
      </div>
    );
  }

  const today = new Date();

  return (
    <div className="p-4 space-y-4">

      {/* ── Fichaje del día ── */}
      <FichajeCard user={user} />

      {/* ── Disputas abiertas ── */}
      {myDisputes.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-base">⚠️</span>
            <h3 className="font-bold text-orange-800 text-sm">
              {myDisputes.length === 1 ? 'Tienes una disputa abierta' : `Tienes ${myDisputes.length} disputas abiertas`}
            </h3>
          </div>
          {myDisputes.map(c => {
            const d = toDate(c.date);
            const replyText = disputeReplies[c.id] || '';
            const sending   = !!sendingReply[c.id];
            return (
              <div key={c.id} className="bg-orange-50 border-2 border-orange-300 rounded-2xl overflow-hidden">
                {/* Cabecera */}
                <div className="bg-orange-500 px-4 py-2.5 flex items-center justify-between">
                  <span className="text-white font-bold text-sm">Disputa · Dir. de Operaciones</span>
                  <span className="text-orange-100 text-xs">
                    {d ? d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : ''}
                  </span>
                </div>
                <div className="p-4 space-y-3">
                  {/* Nota del director */}
                  <div className="bg-white border border-orange-200 rounded-xl p-3">
                    <p className="text-xs font-semibold text-orange-700 mb-1">Dir. de Operaciones dice:</p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{c.disputeNote}</p>
                  </div>
                  {/* Respuesta enviada o formulario */}
                  {c.disputeReply ? (
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                      <p className="text-xs font-semibold text-blue-700 mb-1">Tu respuesta:</p>
                      <p className="text-sm text-gray-800 whitespace-pre-wrap">{c.disputeReply}</p>
                    </div>
                  ) : (
                    <>
                      <textarea
                        value={replyText}
                        onChange={e => setDisputeReplies(prev => ({ ...prev, [c.id]: e.target.value }))}
                        rows={3}
                        placeholder="Escribe tu explicación aquí..."
                        className="w-full px-3 py-2.5 border border-orange-300 rounded-xl focus:ring-2 focus:ring-orange-500 text-sm resize-none bg-white"
                      />
                      <button
                        onClick={() => handleSendReply(c.id)}
                        disabled={sending || !replyText.trim()}
                        className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white font-semibold rounded-xl text-sm transition-colors"
                      >
                        {sending ? 'Enviando...' : 'Enviar respuesta'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Resumen semana / mes */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border">
        <h2 className="text-base font-bold text-gray-900 mb-3">Mi rendimiento</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs font-semibold text-gray-500 mb-1">Esta semana</p>
            <p className={`text-3xl font-bold ${weekStats.pct >= 100 ? 'text-green-600' : weekStats.pct > 0 ? 'text-amber-600' : 'text-gray-800'}`}>
              {weekStats.pct}%
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {weekStats.completed}/{weekStats.assigned} órdenes · {weekStats.days} día{weekStats.days !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="text-xs font-semibold text-gray-500 mb-1">Este mes</p>
            <p className={`text-3xl font-bold ${monthStats.pct >= 100 ? 'text-green-600' : monthStats.pct > 0 ? 'text-amber-600' : 'text-gray-800'}`}>
              {monthStats.pct}%
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {monthStats.completed}/{monthStats.assigned} órdenes · {monthStats.days} día{monthStats.days !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* Vista semanal */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border">
        <h3 className="text-sm font-bold text-gray-800 mb-3">Vista semanal</h3>
        {/* Sin min-w forzado — con 7 columnas, un mínimo fijo obligaba a
            desplazar horizontalmente en móviles estrechos; w-full + padding
            ajustado ya cabe sin scroll en los anchos de pantalla habituales,
            y overflow-x-auto se deja como red de seguridad para el caso
            extremo de una pantalla muy pequeña. */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                {weekDays.map(dia => {
                  const isT = isSameDay(dia, today);
                  return (
                    <th key={dia.toDateString()}
                      className={`text-center p-1 font-semibold text-xs ${isT ? 'text-red-700' : 'text-gray-500'}`}>
                      <div className={`uppercase ${isT ? 'text-red-600' : ''}`}>
                        {dia.toLocaleDateString('es-ES', { weekday: 'short' })}
                      </div>
                      <div className={`mt-0.5 w-6 h-6 mx-auto rounded-full flex items-center justify-center font-bold ${isT ? 'bg-red-600 text-white' : ''}`}>
                        {dia.getDate()}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              <tr>
                {weekDays.map(dia => {
                  const closure = myClosures.find(c => isSameDay(toDate(c.date), dia));
                  const pct     = closure ? getCompletionPct(closure.assigned, closure.completed) : null;
                  const isT     = isSameDay(dia, today);
                  return (
                    <td key={dia.toDateString()} className={`text-center p-1 ${isT ? 'bg-red-50 rounded-xl' : ''}`}>
                      {closure ? (
                        <div>
                          <span className={`inline-block text-xs font-bold px-1.5 py-0.5 rounded-md ${pctBadge(pct)}`}>
                            {pct >= 100 ? '✓ 100%' : `${pct}%`}
                          </span>
                          <p className="text-xs text-gray-400 mt-0.5">{closure.completed}/{closure.assigned}</p>
                        </div>
                      ) : (
                        <span className="text-gray-200 text-xl leading-none">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Calendario mensual */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-gray-800">Calendario mensual</h3>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCalendarMonth(m => { const d = new Date(m); d.setMonth(d.getMonth() - 1); return d; })}
              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 font-bold leading-none"
            >‹</button>
            <span className="text-xs font-semibold text-gray-700 capitalize px-1 min-w-[90px] text-center">
              {calendarMonth.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
            </span>
            <button
              onClick={() => setCalendarMonth(m => { const d = new Date(m); d.setMonth(d.getMonth() + 1); return d; })}
              className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 font-bold leading-none"
            >›</button>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {calendarData.map(({ date, isToday, isFuture, closure, ausencia }) => {
            const pct = closure ? getCompletionPct(closure.assigned, closure.completed) : null;
            const tipoAus = ausencia ? getTipoAusencia(ausencia.tipo) : null;
            const pendiente = ausencia?.estado === 'pendiente';
            const title = ausencia
              ? `${tipoAus.label}${pendiente ? ' (pendiente de aprobar)' : ausencia.estado === 'rechazada' ? ' (rechazada)' : ''}${ausencia.notas ? ` — ${ausencia.notas}` : ''}`
              : closure
              ? `${closure.completed}/${closure.assigned} órdenes — ${pct}%`
              : date.toLocaleDateString('es-ES');
            return (
              <div
                key={date.toDateString()}
                title={title}
                className={`
                  flex-none w-9 h-9 rounded-lg flex flex-col items-center justify-center
                  text-[11px] font-bold leading-none select-none
                  ${ausencia ? `${tipoAus.calBg} ${pendiente ? 'opacity-50 border-2 border-dashed border-white' : ''}` :
                    isFuture ? 'text-gray-200 bg-transparent' :
                    closure   ? pctColor(pct) :
                               'bg-gray-100 text-gray-400'}
                  ${isToday && !closure && !ausencia ? 'ring-2 ring-red-400 ring-offset-1' : ''}
                  ${isToday && (closure || ausencia) ? 'ring-2 ring-white ring-offset-1' : ''}
                `}
              >
                <span>{date.getDate()}</span>
                {ausencia ? (
                  <span className="text-[8px] opacity-90 mt-0.5 leading-none">{pendiente ? '⏳' : tipoAus.label.split(' ')[0]}</span>
                ) : closure && (
                  <span className="text-[9px] opacity-75 mt-0.5">{pct}%</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-gray-400">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500 inline-block" />100%</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-200 inline-block" />75–99%</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-400 inline-block" />1–74%</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-400 inline-block" />0%</span>
        </div>
        {ausencias.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-400">
            {[...new Set(ausencias.map(a => a.tipo))].map(tipoId => {
              const t = getTipoAusencia(tipoId);
              return (
                <span key={tipoId} className="flex items-center gap-1">
                  <span className={`w-3 h-3 rounded ${t.dot} inline-block`} />{t.label}
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Historial de fichajes (art. 34.9 ET) */}
      <MiHistorialFichajes user={user} />

      <button
        onClick={() => setShowAusenciaModal(true)}
        className="w-full flex items-center justify-center gap-2 py-3 bg-white border-2 border-dashed border-gray-200 hover:border-red-300 hover:bg-red-50 text-gray-600 hover:text-red-700 font-semibold rounded-2xl text-sm transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
        Registrar ausencia
      </button>

      {showAusenciaModal && (
        <RegistrarAusenciaModal
          user={user}
          onClose={() => setShowAusenciaModal(false)}
          onSaved={() => { setShowAusenciaModal(false); cargarAusencias(); }}
        />
      )}

      {/* Historial */}
      {myClosures.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 text-sm">Historial de cierres</h3>
            {myClosures.some(c => c.disputeStatus === 'open') && (
              <span className="text-xs text-orange-600 font-semibold flex items-center gap-1">
                <span>⚠️</span> Disputas pendientes
              </span>
            )}
          </div>
          <div className="divide-y divide-gray-50">
            {myClosures.slice(0, 20).map(c => {
              const pct     = getCompletionPct(c.assigned, c.completed);
              const d       = toDate(c.date);
              const hasDispute = c.disputeStatus === 'open';
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedClosure(c)}
                  className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 active:bg-gray-100 transition-colors ${
                    hasDispute ? 'border-l-4 border-orange-400 bg-orange-50/50' : ''
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-800 capitalize">
                        {d ? d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' }) : '—'}
                      </p>
                      {hasDispute && (
                        <span className="text-xs bg-orange-100 text-orange-700 font-bold px-1.5 py-0.5 rounded-full flex-shrink-0">
                          ⚠ Disputa
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{c.completed}/{c.assigned} órdenes</p>
                    {hasDispute && (
                      <p className="text-xs text-orange-700 mt-0.5 truncate italic">"{c.disputeNote}"</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${pctBadge(pct)}`}>
                      {pct}%
                    </span>
                    <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-10 text-gray-400">
          <div className="text-5xl mb-3">📊</div>
          <p className="font-semibold">Sin cierres registrados aún</p>
          <p className="text-sm mt-1">Cierra tu primera ruta para ver las estadísticas aquí</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>
      )}

      {/* Notificaciones */}
      <NotificationCard user={user} />

      {/* Guía de usuario */}
      <button
        onClick={() => window.open('/guia-usuario.html', '_blank', 'noopener,noreferrer')}
        className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3.5 flex items-center gap-3 hover:bg-gray-50 active:bg-gray-100 transition-colors text-left"
      >
        <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800">Guía de usuario</p>
          <p className="text-xs text-gray-400 mt-0.5">Cómo usar la app, IA y más</p>
        </div>
        <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Modal de detalle */}
      {selectedClosure && (
        <ClosureDetailModal
          closure={selectedClosure}
          onClose={() => setSelectedClosure(null)}
        />
      )}
    </div>
  );
}
